import { useEffect, useState, type FormEvent } from 'react';
import { useT } from '../i18n';
import { pollsApi, type PollResults, type PublicPoll } from '../pollsApi';

/**
 * Anketa ve výloze (#29).
 *
 * Ukáže právě běžící anketu (nejvýš jednu). Kdo ještě nehlasoval, vidí
 * odpovědi a tlačítko; kdo hlasoval (podle cookie/účtu), rovnou výsledky.
 *
 * BEZPEČNOST TEXTU: otázka i odpovědi se vykreslují jako PROSTÝ TEXT přes
 * `{...}` – React je escapuje. Žádný `dangerouslySetInnerHTML`, takže i kdyby
 * admin do textu napsal HTML, do stránky se jako kód nedostane (vzor LayerDoc).
 *
 * Selže-li načtení, komponenta se prostě nevykreslí – prázdná anketa v patičce
 * vypadá hůř než žádná.
 */
export function PollWidget() {
  const { t } = useT();
  const [poll, setPoll] = useState<PublicPoll | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const [results, setResults] = useState<PollResults | null>(null);
  const [voted, setVoted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [thanks, setThanks] = useState(false);

  useEffect(() => {
    let alive = true;
    void pollsApi
      .active()
      .then((state) => {
        if (!alive) return;
        setPoll(state.poll);
        if (state.hasVoted === true && state.results !== undefined) {
          setVoted(true);
          setResults(state.results);
        }
      })
      .catch(() => {
        if (alive) setPoll(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (poll === null || choice === null || submitting) return;
    setSubmitting(true);
    try {
      const response = await pollsApi.vote(poll.id, choice);
      setResults(response.results);
      setVoted(true);
      // `counted: false` = návštěvník už hlasoval dřív; poděkování dává smysl
      // jen za právě odevzdaný hlas.
      setThanks(response.counted);
    } catch {
      // Necháme formulář být – návštěvník to může zkusit znovu.
      setSubmitting(false);
    }
  }

  if (poll === null) return null;

  return (
    <section className="poll-widget" aria-label={t('poll.title')}>
      <style>{POLL_WIDGET_STYLES}</style>
      <span className="poll-eyebrow">{t('poll.title')}</span>
      <h3 className="poll-question">{poll.question}</h3>

      {voted && results !== null ? (
        <PollResultsView results={results} thanks={thanks} t={t} />
      ) : (
        <form className="poll-form" onSubmit={(e) => void submit(e)}>
          <ul className="poll-choices">
            {poll.options.map((option) => (
              <li key={option.id}>
                <label className={choice === option.id ? 'poll-choice is-selected' : 'poll-choice'}>
                  <input
                    type="radio"
                    name={`poll-${poll.id}`}
                    value={option.id}
                    checked={choice === option.id}
                    onChange={() => setChoice(option.id)}
                  />
                  <span>{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
          <button type="submit" className="button primary" disabled={choice === null || submitting}>
            {t('poll.vote')}
          </button>
        </form>
      )}
    </section>
  );
}

function PollResultsView({
  results,
  thanks,
  t,
}: {
  results: PollResults;
  thanks: boolean;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  return (
    <div className="poll-results-view">
      {thanks && <p className="poll-thanks">{t('poll.thanks')}</p>}
      <ul className="poll-results">
        {results.options.map((option) => {
          const pct =
            results.totalVotes === 0 ? 0 : Math.round((option.votes / results.totalVotes) * 100);
          return (
            <li key={option.id} className="poll-result">
              <div className="poll-result-label">
                <span>{option.label}</span>
                <span className="poll-pct">{pct} %</span>
              </div>
              <div className="poll-bar" aria-hidden="true">
                <div className="poll-bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
      <p className="poll-total">{t('poll.totalVotes', { count: results.totalVotes })}</p>
    </div>
  );
}

const POLL_WIDGET_STYLES = `
.poll-widget {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 22rem;
}
.poll-eyebrow {
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--accent);
}
.poll-question { margin: 0 0 0.4rem; font-size: 1rem; line-height: 1.35; }
.poll-choices { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.poll-choice {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 8px);
  cursor: pointer;
  font-size: 0.92rem;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.poll-choice:hover { border-color: var(--border-strong); }
.poll-choice.is-selected { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 8%, transparent); }
.poll-form .button { margin-top: 0.6rem; align-self: flex-start; }
.poll-results { list-style: none; margin: 0.3rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
.poll-result-label { display: flex; justify-content: space-between; gap: 0.5rem; font-size: 0.9rem; }
.poll-pct { color: var(--muted); font-variant-numeric: tabular-nums; }
.poll-bar { height: 8px; border-radius: 999px; background: var(--border); overflow: hidden; margin-top: 0.25rem; }
.poll-bar-fill { height: 100%; background: var(--accent); border-radius: 999px; transition: width 0.4s ease; }
.poll-thanks { margin: 0 0 0.3rem; font-size: 0.9rem; color: var(--accent); font-weight: 500; }
.poll-total { margin: 0.5rem 0 0; font-size: 0.82rem; color: var(--muted); }
`;
